import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import { orange, red } from "@mui/material/colors";

import { withTranslation } from "react-i18next";
import { debounce } from "lodash";
import { format } from "date-fns";

import fpDeliveryStatusService from "@plannel/services/fp/fp-delivery-status-service";
import VersionSearchGroup from "@plannel/pages/factory-plan/common/component/VersionSearchGroup";
import PeriodSearchGroup from "@plannel/pages/factory-plan/common/component/PeriodSearchGroup";
import SearchOptsGroupRetrieve from "@plannel/pages/factory-plan/common/component/SearchOptsGroupRetrieve";
import FpGridSetting, { buildFlatReportStatusBar, readHide } from "@plannel/pages/factory-plan/common/grid";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import CustomDetailHeader from "@plannel/components/aggrid/CustomDetailHeader";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import Snackbar from "@plannel/components/Snackbar";
import { DownloadButton } from "@plannel/components/ActionIconButton";
import ColorUtil from "@plannel/pages/utils/color-util";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";

const { snackBarMessageColor } = ColorUtil;

// old FP_SRH_RANGE.attr01Val(DB 단일 고정값) 이식 — fp-report-screen-guide.md §A-3c/§B와 동일 결정.
const SEARCH_ZONE_DAYS = 9;

// old SearchOptsGroup이 이 화면에서 실제로 쓰던 필드 3개 —
// DeliveryStatusQueryRepository#makeWhereClause가 customerCdNm/itemCdNm/woCdNm 전부 contains 조건으로 소비.
const SEARCH_FIELD_LABELS = { customerCdNm: "FP_CUSTOMER", itemCdNm: "FP_ITEM", woCdNm: "FP_ORDER_CD" };
const SEARCH_FIELD_KEYS = Object.keys(SEARCH_FIELD_LABELS);

const PARAM_DATE_FORMAT = "yyyyMMdd";

// old InputField name="delayParam"(react-hook-form radio, 기본값 'A') — 백엔드 delayType 파라미터로
// 그대로 전달된다(DeliveryStatusQueryRepository의 isLate = "D".equals(delayType)).
const DELAY_OPTIONS = [
  { value: "A", label: "ALL" },
  { value: "D", label: "FP_DELIVERY_DELAY" }
];

const ITEM_NM_HIDE_KEY = "deliveryStatusItemNmColsHide";
const CUSTOMER_NM_HIDE_KEY = "deliveryStatusCustomerNmColsHide";

const DeliveryStatus = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);

  const [versionState, setVersionState] = useState({
    planScope: "", planningDate: null, versionCd: "", plantCd: [], selectedVersion: null
  });
  const [periodState, setPeriodState] = useState({
    startDate: null, endDate: null, bucketType: "", isEntirePlanPeriod: true
  });
  const [delayType, setDelayType] = useState("A");

  const customerCdNmRef = useRef("");
  const itemCdNmRef = useRef("");
  const woCdNmRef = useRef("");

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);

  const latestReqRef = useRef(0);
  const searchStateRef = useRef({ versionState, periodState, delayType });
  searchStateRef.current = { versionState, periodState, delayType };

  const debouncedRetrieveRef = useRef();
  if (!debouncedRetrieveRef.current) {
    debouncedRetrieveRef.current = debounce(() => retrieveDeliveryStatus(), 200);
  }
  useEffect(() => () => debouncedRetrieveRef.current.cancel(), []);

  const defaultGridMemo = useMemo(() => {
    const base = FpGridSetting({ title, viewName });
    return {
      ...base,
      defaultColDef: { ...base.defaultColDef, editable: false },
      treeData: true,
      // old dataProvider.setRows(data,"treeId",...) — treeId가 "003"(수요)/"003-01"(작업주문) 형태의
      // 경로 문자열이라, "-"로 나눈 세그먼트가 곧 AG Grid Tree Data의 경로가 된다(DeliveryStatusService
      // #toTreeDtos가 이 형식으로 채번). 트리 구조 자체가 이 한 줄로 old TreeGrid의 부모(수요)/자식
      // (작업주문) 계층과 동일하게 재현된다.
      getDataPath: (data) => data.treeId.split("-"),
      groupDefaultExpanded: -1
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // old orderDueDate styleCallback — dueDate(=orderDueDate)가 endTs(버전 계획종료, 숨김 필드)보다
  // 늦으면 지연으로 보고 강조. planEndTs(행별 계획종료)가 아니라 endTs 비교인 것에 주의(old 그대로).
  // 컬럼 type의 centerAligned(cellStyle 객체)는 colDef가 cellStyle 함수를 직접 지정하면 덮어써져
  // 사라진다 — 강조색과 가운데 정렬을 같이 내려면 여기서 직접 합쳐야 한다.
  const CENTER_ALIGN_STYLE = { display: "flex", justifyContent: "center", alignItems: "center" };

  const orderDueDateCellStyle = (params) => {
    const dueDate = params.data?.orderDueDate;
    const versionEndTs = params.data?.endTs;
    const isLate = dueDate && versionEndTs && new Date(dueDate).getTime() > new Date(versionEndTs).getTime();
    return isLate ? { ...CENTER_ALIGN_STYLE, color: red[500] } : CENTER_ALIGN_STYLE;
  };

  // old lateness styleCallback — 지연일수(lateness) > 0이면 강조.
  const latenessCellStyle = (params) => (
    (params.data?.lateness ?? 0) > 0 ? { color: red[500] } : null
  );

  // old planEndTs styleCallback — lateQty>0이면 경고, lateQty>0 && shortQty>0이면 에러(더 진한 강조).
  const planEndTsCellStyle = (params) => {
    const lateQty = params.data?.lateQty ?? 0;
    const shortQty = params.data?.shortQty ?? 0;
    if (lateQty > 0 && shortQty > 0) return { ...CENTER_ALIGN_STYLE, backgroundColor: red[100] };
    if (lateQty > 0) return { ...CENTER_ALIGN_STYLE, backgroundColor: orange[100] };
    return CENTER_ALIGN_STYLE;
  };

  // old shortQty styleCallback — 부족 수량(shortQty) > 0이면 강조.
  const shortQtyCellStyle = (params) => (
    (params.data?.shortQty ?? 0) > 0 ? { color: red[500] } : null
  );

  // old gridItems 1:1 대조 — treeId/orderCd는 트리 구조(getDataPath/autoGroupColumnDef)로 흡수되고,
  // itemCd/itemNm·customerCd/customerNm은 old의 horizontal column group(그룹키/화살표 토글)을
  // CustomDetailHeader로 재현(§A-6b golden reference PlanResult.js의 leaf 필드 토글 패턴과 동일 —
  // 이 화면은 rowGroup 대상이 아니라 순수 데이터 컬럼이라 buildGroupColumnPair(showRowGroup 전제)가
  // 아니라 이 방식을 쓴다). 나머지(orderDueDate~endTs, 9개)는 평범한 leaf 컬럼.
  const columnDefs = useMemo(() => [
    {
      headerName: "FP_ITEM_CD", field: "itemCd", colId: "itemCd", width: 140,
      headerComponent: CustomDetailHeader,
      headerComponentParams: { viewName, colsHide: readHide(reduxViewState, ITEM_NM_HIDE_KEY), reduxKey: ITEM_NM_HIDE_KEY, detailColumns: ["itemNm"] }
    },
    { headerName: "FP_ITEM_NM", field: "itemNm", colId: "itemNm", width: 170, hide: readHide(reduxViewState, ITEM_NM_HIDE_KEY) },
    { headerName: "FP_START_TS", field: "planStartTs", colId: "planStartTs", width: 180, type: ["dateTimeColumn", "centerAligned"] },
    { headerName: "FP_END_TS", field: "planEndTs", colId: "planEndTs", width: 180, type: ["dateTimeColumn", "centerAligned"], cellStyle: planEndTsCellStyle },
    { headerName: "FP_DUE_DT", field: "orderDueDate", colId: "orderDueDate", width: 180, type: ["dateTimeColumn", "centerAligned"], cellStyle: orderDueDateCellStyle },
    { headerName: "DAYS_LATE", field: "lateness", colId: "lateness", width: 110, type: ["numberColumn", "rightAligned"], cellStyle: latenessCellStyle },
    { headerName: "PRIORITY", field: "orderPriority", colId: "orderPriority", width: 110, type: ["numberColumn", "rightAligned"] },
    {
      headerName: "FP_CUSTOMER_CD", field: "customerCd", colId: "customerCd", width: 140,
      headerComponent: CustomDetailHeader,
      headerComponentParams: { viewName, colsHide: readHide(reduxViewState, CUSTOMER_NM_HIDE_KEY), reduxKey: CUSTOMER_NM_HIDE_KEY, detailColumns: ["customerNm"] }
    },
    { headerName: "FP_CUSTOMER_NM", field: "customerNm", colId: "customerNm", width: 160, hide: readHide(reduxViewState, CUSTOMER_NM_HIDE_KEY) },
    { headerName: "FP_REQUEST_QTY", field: "requestQty", colId: "requestQty", width: 110, type: ["numberColumn", "rightAligned"] },
    { headerName: "ON_TIME_QTY", field: "onTimeQty", colId: "onTimeQty", width: 110, type: ["numberColumn", "rightAligned"] },
    { headerName: "LATE_QTY", field: "lateQty", colId: "lateQty", width: 110, type: ["numberColumn", "rightAligned"] },
    { headerName: "SHORTAGE_QTY", field: "shortQty", colId: "shortQty", width: 110, type: ["numberColumn", "rightAligned"], cellStyle: shortQtyCellStyle },
    { headerName: "plan_end_ts", field: "endTs", colId: "endTs", width: 130, hide: true, type: ["dateTimeColumn"] }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);

  const autoGroupColumnDef = useMemo(() => ({
    headerName: t("FP_ORDER_CD"),
    minWidth: 280,
    pinned: "left",
    type: ["nonEditableColumn"],
    valueGetter: (params) => params.data?.orderCd ?? params.node?.key
  }), [t]);

  const toParamDate = (date) => (date ? format(date, PARAM_DATE_FORMAT) : null);

  const retrieveDeliveryStatus = () => {
    const { versionState: version, periodState: period, delayType: delay } = searchStateRef.current;

    if (!version.planScope || !version.versionCd) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_VERSION") });
      return;
    }
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_PLAN_PERIOD") });
      return;
    }

    const reqId = ++latestReqRef.current;
    const params = {
      viewId: viewName,
      planScope: version.planScope,
      versionCd: version.versionCd,
      plantCds: version.plantCd,
      customerCdNm: customerCdNmRef.current,
      itemCdNm: itemCdNmRef.current,
      woCdNm: woCdNmRef.current,
      fromDt: toParamDate(period.startDate),
      toDt: toParamDate(period.endDate),
      usePlanPeriod: !!period.isEntirePlanPeriod,
      delayType: delay
    };

    gridRef.current?.api?.setGridOption("loading", true);

    fpDeliveryStatusService.getDeliveryStatus(params)
      .then(response => {
        if (reqId !== latestReqRef.current) return;

        const rows = response.data ?? [];
        setRowData(rows);

        if (rows.length === 0) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
      })
      .catch(e => {
        if (reqId === latestReqRef.current) setRowData([]);
        debug(e);
      })
      .finally(() => {
        if (reqId === latestReqRef.current) gridRef.current?.api?.setGridOption("loading", false);
      });
  };

  const onGridReady = (params) => {
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  };

  const statusBar = useMemo(() => buildFlatReportStatusBar(), []);

  useEffect(() => {
    const version = versionState.selectedVersion;
    if (!version?.startTs) {
      setPeriodState(prev => (prev.startDate || prev.endDate) ? { ...prev, startDate: null, endDate: null } : prev);
      return;
    }

    const startDate = new Date(version.startTs);
    const endDate = new Date(startDate.getTime() + SEARCH_ZONE_DAYS * 24 * 60 * 60 * 1000);
    setPeriodState(prev => ({ ...prev, startDate, endDate }));

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.selectedVersion]);

  useEffect(() => {
    if (versionState.versionCd) return;

    debouncedRetrieveRef.current.cancel();
    setRowData([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.versionCd]);

  useEffect(() => {
    const version = searchStateRef.current.versionState;
    const period = searchStateRef.current.periodState;
    if (!version.planScope || !version.versionCd) return;
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) return;

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodState.startDate, periodState.endDate, periodState.isEntirePlanPeriod, versionState.plantCd, delayType]);

  return (
    <>
      <FilterContainer>
        <VersionSearchGroup
          value={versionState}
          onChange={(partial) => setVersionState(prev => ({ ...prev, ...partial }))}
        />
        <PeriodSearchGroup
          value={periodState}
          onChange={(partial) => setPeriodState(prev => ({ ...prev, ...partial }))}
          useBucket={false}
        />
        <SearchOptsGroupRetrieve
          fieldRefs={{ customerCdNm: customerCdNmRef, itemCdNm: itemCdNmRef, woCdNm: woCdNmRef }}
          fieldKeys={SEARCH_FIELD_KEYS}
          labels={SEARCH_FIELD_LABELS}
          retrieve={retrieveDeliveryStatus}
        />
        <RadioGroup row value={delayType} onChange={(e) => setDelayType(e.target.value)} sx={{ ml: 1.5 }}>
          {DELAY_OPTIONS.map((option) => (
            <FormControlLabel key={option.value} value={option.value} control={<Radio size="small" />} label={t(option.label)} />
          ))}
        </RadioGroup>
        <Box sx={{ display: "flex" }}>
          <DownloadButton
            onClick={() => gridRef.current?.api?.exportDataAsExcel({ fileName: `${t(title)}.xlsx`, sheetName: t(title) })}
          />
        </Box>
      </FilterContainer>
      <Box id="fp-delivery-status-grid" className="ag-theme-balham grid-area" sx={{ flex: 1, minHeight: 0, mt: 1, pb: 1 }}>
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          autoGroupColumnDef={autoGroupColumnDef}
          {...defaultGridMemo}
          statusBar={statusBar}
          onGridReady={onGridReady}
        />
      </Box>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(DeliveryStatus);
